import { useEffect } from 'react';

/**
 * SEO Helper - Updates document head with dynamic meta tags
 * Usage: <SEOHead title="Page Title" description="Page description" />
 */
const SEOHead = ({ 
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  schema
}) => {
  useEffect(() => {
    const baseTitle = 'Bravo CinemaStreaming';
    const fullTitle = title ? `${title} | ${baseTitle}` : `${baseTitle} | Ópera, Ballet y Conciertos en Cines`;
    
    // Update document title
    document.title = fullTitle;
    
    // Helper to update or create meta tag
    const updateMeta = (selector, content, attribute = 'content') => {
      if (!content) return;
      let element = document.querySelector(selector);
      if (element) {
        element.setAttribute(attribute, content);
      } else {
        // Create new meta tag if doesn't exist
        const meta = document.createElement('meta');
        const [attr, value] = selector.replace(/[\[\]'"]/g, '').split('=');
        if (attr === 'name' || attr === 'property') {
          meta.setAttribute(attr, value);
          meta.setAttribute('content', content);
          document.head.appendChild(meta);
        }
      }
    };
    
    // Update standard meta tags
    if (description) {
      updateMeta('meta[name="description"]', description);
    }
    
    if (keywords) {
      updateMeta('meta[name="keywords"]', keywords);
    }
    
    // Update Open Graph tags
    updateMeta('meta[property="og:title"]', fullTitle);
    if (description) updateMeta('meta[property="og:description"]', description);
    if (image) updateMeta('meta[property="og:image"]', image);
    if (url) updateMeta('meta[property="og:url"]', url);
    if (type) updateMeta('meta[property="og:type"]', type);
    
    // Update Twitter Card tags
    updateMeta('meta[name="twitter:title"]', fullTitle);
    if (description) updateMeta('meta[name="twitter:description"]', description);
    if (image) updateMeta('meta[name="twitter:image"]', image);
    
    // Update canonical URL
    if (url) {
      let canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute('href', url);
      }
    }
    
    // Add JSON-LD Schema if provided
    if (schema) {
      // Remove any existing dynamic schema
      const existingSchema = document.querySelector('script[data-seo-schema]');
      if (existingSchema) {
        existingSchema.remove();
      }
      
      const schemaScript = document.createElement('script');
      schemaScript.type = 'application/ld+json';
      schemaScript.setAttribute('data-seo-schema', 'true');
      schemaScript.textContent = JSON.stringify(schema);
      document.head.appendChild(schemaScript);
    }
    
    // Cleanup function
    return () => {
      const dynamicSchema = document.querySelector('script[data-seo-schema]');
      if (dynamicSchema) {
        dynamicSchema.remove();
      }
    };
  }, [title, description, keywords, image, url, type, schema]);
  
  return null;
};

/**
 * Generate Event Schema for content pages
 */
export const generateEventSchema = (content) => {
  if (!content) return null;
  
  return {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": content.title,
    "description": content.synopsis,
    "image": content.image || content.hero_image,
    "eventStatus": "https://schema.org/EventScheduled",
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
    "performer": {
      "@type": "PerformingGroup",
      "name": content.composer || "Varios artistas"
    },
    "organizer": {
      "@type": "Organization",
      "name": "Bravo CinemaStreaming",
      "url": "https://www.cinemastreaming.es"
    },
    "location": {
      "@type": "Place",
      "name": content.theater || "Cines adheridos",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "ES"
      }
    },
    ...(content.premiere_date && {
      "startDate": content.premiere_date
    }),
    "offers": {
      "@type": "Offer",
      "url": `https://www.cinemastreaming.es/content/${content.id}`,
      "availability": "https://schema.org/InStock",
      "priceCurrency": "EUR"
    }
  };
};

/**
 * Generate BreadcrumbList Schema
 */
export const generateBreadcrumbSchema = (items) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };
};

/**
 * Generate FAQ Schema for pages with FAQs
 */
export const generateFAQSchema = (faqs) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

/**
 * Generate Product Schema for Dispositivo Bravo page
 */
export const generateProductSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Dispositivo Bravo",
    "description": "Sistema de streaming profesional para exhibición de contenido cultural en cines. Compatible con señal en directo y contenido grabado.",
    "brand": {
      "@type": "Brand",
      "name": "Bravo CinemaStreaming"
    },
    "manufacturer": {
      "@type": "Organization",
      "name": "Versión Digital S.L."
    },
    "image": "https://www.cinemastreaming.es/dispositivo-bravo.jpg",
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "EUR",
      "lowPrice": "0",
      "highPrice": "299",
      "offerCount": "3"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "50"
    }
  };
};

export default SEOHead;
